import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { Button, Input } from "@/shared/ui";
import { getUserDocuments, addDocumentToCase } from "@/shared/db";
import { toast } from "sonner";
import { getCountryName, formatDateShort } from "@/shared/core";
import { CountryFlag } from "@/shared/ui";
import { motion } from "framer-motion";

interface Document {
  id: string;
  title: string;
  document_type: string;
  country: string;
  status: string;
  created_at: string;
}

interface AssignDocumentToCaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  caseId: string;
  caseTitle: string;
  userId: string;
}

export const AssignDocumentToCaseModal: React.FC<
  AssignDocumentToCaseModalProps
> = ({ isOpen, onClose, onSuccess, caseId, caseTitle, userId }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<Document[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [purpose, setPurpose] = useState("");

  useEffect(() => {
    if (isOpen && userId) {
      loadDocuments();
    }
  }, [isOpen, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    filterDocuments();
  }, [documents, searchTerm, typeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDocuments = async () => {
    try {
      setLoadingDocs(true);
      const docs = await getUserDocuments(userId);
      setDocuments(docs as Document[]);
    } catch (error: unknown) {
      toast.error(t("lawyer.assignDocument.error.loadDocs"));
      console.error(error);
    } finally {
      setLoadingDocs(false);
    }
  };

  const filterDocuments = () => {
    let filtered = [...documents];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (doc) =>
          doc.title.toLowerCase().includes(term) ||
          doc.document_type.toLowerCase().includes(term),
      );
    }

    if (typeFilter !== "all") {
      filtered = filtered.filter((doc) => doc.document_type === typeFilter);
    }

    setFilteredDocuments(filtered);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedDocument) {
      toast.error(t("lawyer.assignDocument.error.noDocument"));
      return;
    }

    try {
      setLoading(true);

      await addDocumentToCase(
        caseId,
        selectedDocument,
        userId,
        purpose || undefined,
      );

      toast.success(t("lawyer.assignDocument.success"));
      onSuccess();
      handleClose();
    } catch (error: unknown) {
      toast.error(t("lawyer.assignDocument.error.assign"));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setSelectedDocument("");
      setSearchTerm("");
      setTypeFilter("all");
      setPurpose("");
      onClose();
    }
  };

  const getUniqueDocumentTypes = () => {
    const types = new Set(documents.map((doc) => doc.document_type));
    return Array.from(types).sort();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Background overlay */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal container */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative bg-white rounded-xl shadow-2xl max-w-3xl w-full z-10 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-linear-to-r from-tiber-700 to-tiber-500 px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold text-white">
                  {t("lawyer.assignDocument.title")}
                </h3>
                <p className="text-sm text-tiber-100 mt-1">
                  {t("lawyer.assignDocument.subtitle")} {caseTitle}
                </p>
              </div>
              <button
                onClick={handleClose}
                disabled={loading}
                className="text-white/80 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-xl disabled:opacity-50"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8">
            {/* Search and Filter */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-tiber-700 mb-2">
                  {t("lawyer.assignDocument.search")}
                </label>
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-titan" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t("lawyer.assignDocument.searchPlaceholder")}
                    className="w-full pl-10 pr-4 py-2 border border-tiber-300 rounded-lg focus:ring-2 focus:ring-tiber-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-tiber-700 mb-2">
                  {t("lawyer.assignDocument.filterByType")}
                </label>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-tiber-300 rounded-lg focus:ring-2 focus:ring-tiber-500 focus:border-transparent"
                >
                  <option value="all">
                    {t("lawyer.assignDocument.allTypes")}
                  </option>
                  {getUniqueDocumentTypes().map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Documents List */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-tiber-700 mb-2">
                {t("lawyer.assignDocument.selectDocument")} *
              </label>
              <div className="border border-tiber-300 rounded-lg max-h-96 overflow-y-auto">
                {loadingDocs ? (
                  <div className="p-8 text-center text-titan">
                    {t("common.loading")}
                  </div>
                ) : filteredDocuments.length === 0 ? (
                  <div className="p-8 text-center text-titan">
                    <DocumentTextIcon className="h-12 w-12 mx-auto mb-2 text-titan" />
                    <p>{t("lawyer.assignDocument.noDocuments")}</p>
                  </div>
                ) : (
                  <div className="divide-y divide-tiber-200">
                    {filteredDocuments.map((doc) => (
                      <label
                        key={doc.id}
                        className={`flex items-center p-4 cursor-pointer hover:bg-romance-50 transition-colors ${
                          selectedDocument === doc.id ? "bg-tiber-50" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="document"
                          value={doc.id}
                          checked={selectedDocument === doc.id}
                          onChange={(e) => setSelectedDocument(e.target.value)}
                          className="h-4 w-4 text-tiber-600 focus:ring-tiber-500 border-tiber-300"
                        />
                        <div className="ml-4 flex-1">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-juridico">
                                {doc.title}
                              </p>
                              <div className="flex items-center gap-3 mt-1">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-tiber-100 text-tiber-800">
                                  {doc.document_type}
                                </span>
                                <div className="flex items-center text-xs text-titan">
                                  <CountryFlag
                                    countryCode={doc.country}
                                    size="1em"
                                    className="mr-1"
                                  />
                                  {getCountryName(doc.country)}
                                </div>
                                <span className="text-xs text-titan">
                                  {formatDateShort(doc.created_at)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Purpose */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-tiber-700 mb-2">
                {t("lawyer.assignDocument.notes")} ({t("common.optional")})
              </label>
              <Input
                type="text"
                value={purpose}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setPurpose(e.target.value)
                }
                placeholder={t("lawyer.assignDocument.notesPlaceholder")}
                disabled={loading}
              />
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-6 border-t border-tiber-100">
              <Button
                type="button"
                variant="ghost"
                onClick={handleClose}
                disabled={loading}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={loading || !selectedDocument}
              >
                {loading
                  ? t("common.assigning")
                  : t("lawyer.assignDocument.assign")}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};
